import { createContext, useContext, useMemo, useState, useCallback, useEffect } from "react";
import { api, apiError, TOKEN_KEY } from "@/lib/api";

const AuthContext = createContext(null);

const mapErr = (e) => {
  const d = apiError(e);
  return ["errExists", "errInvalid", "errLocked"].includes(d) ? d : e?.response?.status === 422 ? "errRequired" : "errGeneric";
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(!localStorage.getItem(TOKEN_KEY));

  useEffect(() => {
    if (!localStorage.getItem(TOKEN_KEY)) return;
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => localStorage.removeItem(TOKEN_KEY)).finally(() => setReady(true));
  }, []);

  const finish = (r) => { localStorage.setItem(TOKEN_KEY, r.data.token); setUser(r.data.user); return { user: r.data.user }; };

  const register = useCallback(async (data) => {
    try { return finish(await api.post("/auth/register", data)); } catch (e) { return { error: mapErr(e) }; }
  }, []);

  const login = useCallback(async (email, password) => {
    try { return finish(await api.post("/auth/login", { email, password })); } catch (e) { return { error: mapErr(e) }; }
  }, []);

  const logout = useCallback(() => { localStorage.removeItem(TOKEN_KEY); setUser(null); }, []);

  const update = useCallback(async (patch) => {
    try { const r = await api.put("/auth/me", patch); setUser(r.data); return { user: r.data }; } catch (e) { return { error: mapErr(e) }; }
  }, []);

  const value = useMemo(() => ({ user, ready, register, login, logout, update }), [user, ready, register, login, logout, update]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
